import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../../services/api';
import './ManualMatchModal.css';

/**
 * ManualMatchModal Component
 * Modal for attorneys to manually match unmatched correspondence
 * Captures AI learning feedback for the Triage Agent
 */
function ManualMatchModal({ correspondence, onClose, onSubmit, isSubmitting }) {
  const { t } = useTranslation('attorney');

  // Form state - correct values
  const [applicationId, setApplicationId] = useState(correspondence?.applicationId || '');
  const [classification, setClassification] = useState(correspondence?.classification || 'general');
  const [subType, setSubType] = useState(correspondence?.subType || '');
  const [jurisdiction, setJurisdiction] = useState(correspondence?.jurisdiction || '');
  const [deadline, setDeadline] = useState(correspondence?.deadline ? correspondence.deadline.split('T')[0] : '');
  const [ipOfficeRef, setIpOfficeRef] = useState(correspondence?.ipOfficeRef || '');

  // Form state - feedback for AI learning
  const [failureCategory, setFailureCategory] = useState('');
  const [infoLocations, setInfoLocations] = useState([]);
  const [notes, setNotes] = useState('');

  // Application search state
  const [applicationSearch, setApplicationSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);

  // Classification options
  const classificationOptions = [
    { value: 'office_action', label: t('attorney.correspondence.types.officeAction', 'Office Action') },
    { value: 'acceptance', label: t('attorney.correspondence.types.acceptance', 'Acceptance') },
    { value: 'opposition', label: t('attorney.correspondence.types.opposition', 'Opposition') },
    { value: 'renewal_notice', label: t('attorney.correspondence.types.renewal', 'Renewal Notice') },
    { value: 'registration', label: t('attorney.correspondence.types.registration', 'Registration') },
    { value: 'general', label: t('attorney.correspondence.types.general', 'General') }
  ];

  // Sub-type options based on classification
  const subTypeOptions = {
    office_action: [
      { value: 'descriptiveness', label: 'Descriptiveness' },
      { value: 'likelihood_of_confusion', label: 'Likelihood of Confusion' },
      { value: 'specimen_issues', label: 'Specimen Issues' },
      { value: 'goods_services', label: 'Goods/Services Classification' },
      { value: 'formality', label: 'Formality Issues' },
      { value: 'other', label: 'Other' }
    ],
    opposition: [
      { value: 'likelihood_of_confusion', label: 'Likelihood of Confusion' },
      { value: 'dilution', label: 'Dilution' },
      { value: 'bad_faith', label: 'Bad Faith' },
      { value: 'other', label: 'Other' }
    ]
  };

  // Jurisdiction options
  const jurisdictionOptions = [
    { value: 'AU', label: 'Australia' },
    { value: 'US', label: 'United States' },
    { value: 'EU', label: 'European Union' },
    { value: 'UK', label: 'United Kingdom' },
    { value: 'NZ', label: 'New Zealand' },
    { value: 'CA', label: 'Canada' },
    { value: 'SG', label: 'Singapore' }
  ];

  // Failure category options - why the AI failed to match
  const failureCategoryOptions = [
    { value: 'unusual_email_format', label: t('attorney.manualMatch.failures.unusualFormat', 'Unusual Email Format') },
    { value: 'new_ip_office', label: t('attorney.manualMatch.failures.newIpOffice', 'New IP Office Format') },
    { value: 'pdf_extraction_failed', label: t('attorney.manualMatch.failures.pdfFailed', 'PDF Extraction Failed') },
    { value: 'no_reference_found', label: t('attorney.manualMatch.failures.noReference', 'No Reference Found') },
    { value: 'ambiguous_reference', label: t('attorney.manualMatch.failures.ambiguous', 'Ambiguous Reference') },
    { value: 'reference_in_attachment', label: t('attorney.manualMatch.failures.inAttachment', 'Reference in Attachment Only') },
    { value: 'new_sender', label: t('attorney.manualMatch.failures.newSender', 'New Sender/Domain') },
    { value: 'other', label: t('attorney.manualMatch.failures.other', 'Other') }
  ];

  // Info location options - where the correct information was found
  const infoLocationOptions = [
    { value: 'subject_line', label: t('attorney.manualMatch.locations.subjectLine', 'Subject Line') },
    { value: 'email_body', label: t('attorney.manualMatch.locations.emailBody', 'Email Body') },
    { value: 'pdf_attachment', label: t('attorney.manualMatch.locations.pdfAttachment', 'PDF Attachment') },
    { value: 'image_attachment', label: t('attorney.manualMatch.locations.imageAttachment', 'Image Attachment') },
    { value: 'sender_domain', label: t('attorney.manualMatch.locations.senderDomain', 'Sender Domain') }
  ];

  // Search for applications
  useEffect(() => {
    const searchApplications = async () => {
      if (applicationSearch.length < 3) {
        setSearchResults([]);
        return;
      }

      setIsSearching(true);
      try {
        const response = await apiClient.get(`/applications?search=${encodeURIComponent(applicationSearch)}&limit=10`);
        setSearchResults(response.data?.applications || response.data || []);
      } catch (error) {
        console.error('Error searching applications:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    const debounce = setTimeout(searchApplications, 300);
    return () => clearTimeout(debounce);
  }, [applicationSearch]);

  // Handle application selection
  const handleSelectApplication = (app) => {
    setSelectedApplication(app);
    setApplicationId(app.applicationId || app.id);
    setSearchResults([]);
    setApplicationSearch('');
  };

  // Handle info location toggle
  const handleInfoLocationToggle = (location) => {
    setInfoLocations(prev =>
      prev.includes(location)
        ? prev.filter(l => l !== location)
        : [...prev, location]
    );
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (!applicationId) {
      alert(t('attorney.manualMatch.errors.applicationRequired', 'Please select an application'));
      return;
    }

    const matchData = {
      correct: {
        applicationId,
        userId: selectedApplication?.userId,
        classification,
        subType: subType || undefined,
        jurisdiction: jurisdiction || undefined,
        deadline: deadline ? new Date(deadline).toISOString() : undefined,
        ipOfficeRef: ipOfficeRef || undefined
      },
      feedback: {
        failureCategory: failureCategory || undefined,
        infoLocations: infoLocations.length > 0 ? infoLocations : undefined,
        notes: notes || undefined
      }
    };

    onSubmit(matchData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="manual-match-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{t('attorney.manualMatch.title', 'Manual Match')}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Email Summary */}
          <div className="modal-section email-summary">
            <div className="summary-item">
              <span className="summary-label">{t('attorney.correspondence.from', 'From')}:</span>
              <span className="summary-value">{correspondence?.from}</span>
            </div>
            <div className="summary-item">
              <span className="summary-label">{t('attorney.correspondence.subject', 'Subject')}:</span>
              <span className="summary-value">{correspondence?.subject}</span>
            </div>
          </div>

          {/* Application Search */}
          <div className="modal-section">
            <h3>{t('attorney.manualMatch.selectApplication', 'Select Application')}</h3>

            {selectedApplication ? (
              <div className="selected-application">
                <div className="selected-app-info">
                  <span className="app-id">{selectedApplication.applicationId || selectedApplication.id}</span>
                  <span className="app-name">{selectedApplication.trademarkName}</span>
                </div>
                <button
                  type="button"
                  className="btn-change"
                  onClick={() => {
                    setSelectedApplication(null);
                    setApplicationId('');
                  }}
                >
                  {t('attorney.manualMatch.change', 'Change')}
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  className="search-input"
                  placeholder={t('attorney.manualMatch.searchPlaceholder', 'Search by application ID or trademark name...')}
                  value={applicationSearch}
                  onChange={(e) => setApplicationSearch(e.target.value)}
                />

                {isSearching && (
                  <div className="search-loading">
                    {t('attorney.manualMatch.searching', 'Searching...')}
                  </div>
                )}

                {searchResults.length > 0 && (
                  <div className="search-results">
                    {searchResults.map((app) => (
                      <div
                        key={app.id}
                        className="search-result-item"
                        onClick={() => handleSelectApplication(app)}
                      >
                        <span className="result-id">{app.applicationId || app.id}</span>
                        <span className="result-name">{app.trademarkName}</span>
                        <span className="result-client">{app.clientName || app.ownerName}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Manual ID entry fallback */}
                <div className="manual-id-entry">
                  <span className="or-divider">{t('attorney.manualMatch.or', 'or enter manually')}</span>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="MTM-2025-00123"
                    value={applicationId}
                    onChange={(e) => setApplicationId(e.target.value)}
                  />
                </div>
              </>
            )}
          </div>

          {/* Classification & Details */}
          <div className="modal-section">
            <h3>{t('attorney.manualMatch.classificationDetails', 'Classification & Details')}</h3>

            <div className="form-grid">
              <div className="form-group">
                <label>{t('attorney.manualMatch.classification', 'Classification')}</label>
                <select
                  value={classification}
                  onChange={(e) => {
                    setClassification(e.target.value);
                    setSubType(''); // Reset sub-type when classification changes
                  }}
                  className="form-select"
                >
                  {classificationOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>{t('attorney.manualMatch.subType', 'Sub-Type')}</label>
                <select
                  value={subType}
                  onChange={(e) => setSubType(e.target.value)}
                  className="form-select"
                  disabled={!subTypeOptions[classification]}
                >
                  <option value="">
                    {subTypeOptions[classification]
                      ? t('attorney.manualMatch.selectSubType', 'Select sub-type...')
                      : t('attorney.manualMatch.notApplicable', 'N/A')}
                  </option>
                  {(subTypeOptions[classification] || []).map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>{t('attorney.manualMatch.jurisdiction', 'Jurisdiction')}</label>
                <select
                  value={jurisdiction}
                  onChange={(e) => setJurisdiction(e.target.value)}
                  className="form-select"
                >
                  <option value="">{t('attorney.manualMatch.selectJurisdiction', 'Select jurisdiction...')}</option>
                  {jurisdictionOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>{t('attorney.manualMatch.deadline', 'Deadline')}</label>
                <input
                  type="date"
                  value={deadline}
                  onChange={(e) => setDeadline(e.target.value)}
                  className="form-input"
                />
              </div>

              <div className="form-group full-width">
                <label>{t('attorney.manualMatch.ipOfficeRef', 'IP Office Reference')}</label>
                <input
                  type="text"
                  value={ipOfficeRef}
                  onChange={(e) => setIpOfficeRef(e.target.value)}
                  className="form-input"
                  placeholder={t('attorney.manualMatch.ipOfficeRefPlaceholder', 'e.g., TM2025/12345')}
                />
              </div>
            </div>
          </div>

          {/* AI Learning Feedback */}
          <div className="modal-section learning-section">
            <h3>{t('attorney.manualMatch.aiLearning', 'AI Learning Feedback')}</h3>
            <p className="section-description">
              {t('attorney.manualMatch.aiLearningDesc', 'Help the AI learn from this correction to improve future matching.')}
            </p>

            <div className="form-group">
              <label>{t('attorney.manualMatch.whyFailed', 'Why did automatic matching fail?')}</label>
              <select
                value={failureCategory}
                onChange={(e) => setFailureCategory(e.target.value)}
                className="form-select"
              >
                <option value="">{t('attorney.manualMatch.selectFailure', 'Select a reason...')}</option>
                {failureCategoryOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>{t('attorney.manualMatch.whereFound', 'Where did you find the correct information?')}</label>
              <div className="checkbox-group">
                {infoLocationOptions.map(opt => (
                  <label key={opt.value} className="checkbox-item">
                    <input
                      type="checkbox"
                      checked={infoLocations.includes(opt.value)}
                      onChange={() => handleInfoLocationToggle(opt.value)}
                    />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>{t('attorney.manualMatch.notes', 'Notes (optional)')}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="form-textarea"
                placeholder={t('attorney.manualMatch.notesPlaceholder', 'Any additional context that might help the AI...')}
                rows={3}
              />
            </div>
          </div>

          {/* Actions */}
          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              {t('attorney.manualMatch.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="btn-submit"
              disabled={isSubmitting || !applicationId}
            >
              {isSubmitting
                ? t('attorney.manualMatch.saving', 'Saving...')
                : t('attorney.manualMatch.saveMatch', 'Save Match')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ManualMatchModal;
